import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { reviews, stats, faqs, contacts } from '@/mocks/home';

export default function AboutPage() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '白云视频',
    description: '白云视频-苹果视频与榴莲视频同类手机免费在线播放 | 看视频app高清不缓冲',
    url: 'https://baiyunvideo.com',
    contactPoint: contacts.map((c) => ({
      '@type': 'ContactPoint',
      contactType: c.type,
      email: c.type === '邮箱' ? c.value : undefined,
    })),
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <Navbar />
      <main>
        <div className="pt-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          {/* Brand Intro */}
          <section className="w-full bg-background-50 py-10 md:py-14">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
                  <div>
                    <h4 className="text-sm font-semibold text-accent-500 uppercase tracking-wide">
                      <a href="#brand-intro">品牌介绍</a>
                    </h4>
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-1">
                      关于白云视频
                    </h1>
                    <p className="mt-4 text-foreground-700 leading-relaxed">
                      <strong>白云视频</strong>是一款致力于为全球华语用户提供<strong>免费高清视频播放</strong>服务的在线平台。
                      与苹果视频、榴莲视频、桃子视频等同类型<strong>看视频app</strong>一样，
                      白云视频专注于<strong>手机看片</strong>体验，提供流畅不缓冲的<strong>高清视频</strong>播放服务。
                    </p>
                    <p className="mt-3 text-foreground-700 leading-relaxed">
                      我们拥有超过<strong>10万部影视资源</strong>，涵盖电影、电视剧、动漫、综艺、纪录片等多个品类，
                      每日更新热门内容，确保用户能第一时间观看到最新、最热的影视作品。
                    </p>
                    <p className="mt-3 text-foreground-700 leading-relaxed">
                      作为领先的<strong>免费影视app</strong>和<strong>在线视频平台</strong>，
                      白云视频始终坚持"用户至上"的理念，采用先进的视频压缩和传输技术，
                      即使在普通网络环境下也能保证<strong>在线看剧</strong>流畅不卡顿。
                      我们的目标是让每一个人都能随时随地享受<strong>免费视频</strong>带来的快乐。
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden aspect-[4/3] bg-background-200">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20interior%20with%20creative%20team%20collaborating%2C%20large%20screens%20displaying%20video%20content%2C%20warm%20ambient%20lighting%2C%20tech%20company%20vibe%2C%20clean%20and%20bright%20workspace%2C%20professional%20creative%20environment&width=800&height=600&seq=about-brand&orientation=landscape"
                      alt="白云视频团队"
                      title="白云视频 - 专业视频平台团队"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full bg-background-100 py-10 md:py-12">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
                  {stats.map((stat) => (
                    <div key={stat.label} className="text-center p-5 rounded-lg bg-background-50">
                      <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">{stat.value}</div>
                      <div className="mt-1 text-sm text-foreground-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Reviews */}
          <section className="w-full bg-background-50 py-10 md:py-14">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-8 md:mb-10">
                  <h4 className="text-sm font-semibold text-accent-500 uppercase tracking-wide">
                    <a href="#reviews">用户口碑</a>
                  </h4>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-1">
                    用户真实评价
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {reviews.map((review) => (
                    <div key={review.id} className="p-5 rounded-lg bg-background-100">
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-background-300'}`}
                          ></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">{review.comment}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs font-medium text-foreground-900">{review.name}</span>
                        <span className="text-xs text-foreground-500">{review.date}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full bg-background-100 py-10 md:py-14">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-8 md:mb-10">
                  <h4 className="text-sm font-semibold text-accent-500 uppercase tracking-wide">
                    <a href="#faq">常见问题</a>
                  </h4>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-1">
                    你可能会问
                  </h2>
                </div>
                <div className="space-y-3">
                  {faqs.map((faq, index) => (
                    <details key={index} className="group bg-background-50 rounded-lg">
                      <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors">
                        {faq.question}
                        <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full bg-background-50 py-10 md:py-14">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-8 md:mb-10">
                  <h4 className="text-sm font-semibold text-accent-500 uppercase tracking-wide">
                    <a href="#contact">联系我们</a>
                  </h4>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-1">
                    商务合作与反馈
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {contacts.map((contact) => (
                    <div key={contact.type} className="p-5 rounded-lg bg-background-100 text-center">
                      <div className="w-12 h-12 flex items-center justify-center mx-auto rounded-full bg-accent-100">
                        <i className={`${contact.icon} text-accent-500 text-xl`}></i>
                      </div>
                      <h3 className="mt-3 text-sm font-semibold text-foreground-900">{contact.type}</h3>
                      <p className="mt-1 text-xs text-foreground-600">{contact.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}